import { z } from 'zod';
import { LABELS } from '../constants';
import { fieldsToValidate, requiredFieldError, validateField } from '../helper';
import { IdentificationType } from '../types';
import { baseResidentialApplicant, identification, photoId } from './model';

// Overrides the `requiredString` definitions inherited from `baseResidentialApplicant`, which order
// the checks as `.min(1).trim()` and so accept a whitespace-only name as an empty string. Trimming
// first makes "   " fail outright. `required_error` is carried over so an omitted name still reports
// the label-specific message rather than Zod's generic "Required".
const requiredIdentificationNames = z.object({
  firstName: z
    .string({ required_error: requiredFieldError(LABELS.FIRST_NAME) })
    .trim()
    .min(1, requiredFieldError(LABELS.FIRST_NAME)),
  lastName: z
    .string({ required_error: requiredFieldError(LABELS.LAST_NAME) })
    .trim()
    .min(1, requiredFieldError(LABELS.LAST_NAME)),
});

export const baseIdentification = z.object({
  identification: identification.superRefine((val, ctx) => {
    validateField(val, ctx, val.type, fieldsToValidate[val.type]);
  }),
});

export const baseIdentificationWithoutMedicare = z.object({
  identification: identification
    .merge(z.object({ type: z.enum([IdentificationType.DRIVING_LICENSE, IdentificationType.PASSPORT]) }))
    .superRefine((val, ctx) => {
      validateField(val, ctx, val.type, fieldsToValidate[val.type]);
    }),
});

export const applicantWithIdentification = baseResidentialApplicant
  .pick({
    firstName: true,
    middleName: true,
    lastName: true,
    address: true,
    type: true,
    dateOfBirth: true,
  })
  .merge(requiredIdentificationNames)
  .merge(baseIdentification);

export const baseIdentificationWithPhotoId = z.object({
  identification: identification
    .merge(
      z.object({
        photoId: photoId.optional(),
      }),
    )
    .superRefine((val, ctx) => {
      validateField(val, ctx, val.type, fieldsToValidate[val.type]);
    }),
});

export const applicantWithPhotoIdentification = applicantWithIdentification.merge(baseIdentificationWithPhotoId);

export const applicantWithNoMedicareIdentification = applicantWithIdentification.merge(
  baseIdentificationWithoutMedicare,
);
